<template>
    <LoadingAnimation animation-style="bars" v-if="isStatusLoading"></LoadingAnimation>

    <div v-else class="w-100 user-modal-register-plan-select-view px-2">
      <div v-if="isLegacy() === true">
        <div class="alert alert-warning gap-10 d-flex flex-column p-5">
          <div class="font-size-sm-200">{{ "You're still on a legacy subscription plan"|trans }}</div>
          <div class="font-size-sm-125">{{ "Your legacy subscription does not allow to buy \"Pay As You Go\" Credits." | trans }}</div>
          <div><a :href="getPricingLink()" class="text-primary">{{ "Learn more" | trans }}</a></div>
        </div>
      </div>
      <div v-if="isLegacy() === false">
        <div class="mb-5 text-center">
            <p class="font-weight-bold font-size-200 mb-0">
                {{ 'Choose Your Plan' | trans }}
            </p>
        </div>

        <div class="d-flex justify-content-center text-center mb-4 ml-n1 mt-n3 flex-wrap">
            <div class="flex-grow-1 ml-1 mt-3 white-space-nowrap">
                <label
                    class="btn btn-white rounded border btn-lg mb-0 py-3 px-4 w-100"
                    :class="{ 'outline-info shadow-info': isIndividualSelected }"
                >
                    <input
                        data-user-modal-input-plan-individual
                        type="radio"
                        class="mr-1 vuetiful-form-radio-button"
                        name="u_plan"
                        value="individual"
                        v-model="selectedPlan"
                    />
                    {{ 'Individual' | trans }}
                    <i class="far fa-user m-l-5"></i> 1
                </label>
            </div>

            <div class="flex-grow-1 ml-1 mt-3 white-space-nowrap position-relative">
                <span
                    v-if="!isTeamsEnabled"
                    class="badge bg-info px-3 position-absolute text-white z-index-1"
                    style="top: -8px; right: 4px"
                    >{{ 'Coming soon' | trans }}</span
                >
                <label
                    class="btn btn-white rounded border btn-lg mb-0 py-3 px-4 w-100"
                    :class="{
                        'outline-info shadow-info': isTeamSelected,
                        'disabled pointer-events-none': !isTeamsEnabled,
                    }"
                >
                    <input
                        data-user-modal-input-plan-team
                        type="radio"
                        class="mr-1 vuetiful-form-radio-button"
                        name="u_plan"
                        value="team"
                        v-model="selectedPlan"
                        :disabled="!isTeamsEnabled"
                    />
                    {{ 'Team' | trans }}
                    <i class="far fa-user-friends m-l-5"></i> 2-50
                    <!-- TODO make team size dynamic? at least make sure this number is accurate for initial release -->
                </label>
            </div>
        </div>

        <div class="mb-4 rounded bg-white border p-4">
            <p class="mb-1 font-size-150">{{ 'Premium' | trans }}</p>
            <div class="color-stripe flex-row w-100 mb-4">
                <div v-for="n in 8" :class="'bg-main-' + n"></div>
            </div>

            <ul class="list-unstyled mb-4">
                <li
                    v-for="(feature, idx) in features"
                    class="py-3 d-flex align-items-baseline"
                    :class="{ 'border-top': idx !== 0 }"
                >
                    <i class="fa fa-check text-success"></i>
                    <span class="pl-1" v-html="feature"></span>
                </li>
            </ul>

            <div class="bg-gray-light rounded p-3 d-flex flex-column align-items-start">
                <strong class="font-size-125">
                    {{ infoBoxTitleText }}
                </strong>
                <div v-html="infoBoxBodyText"></div>
            </div>
        </div>

        <ButtonWithLoadingState
            data-user-modal-button-submit
            class="btn btn-success btn-lg btn-block font-weight-bold text-uppercase rounded mb-4"
            icon="fa fa-arrow-right lh-1"
            :text="'Continue' | trans"
            :is-state-loading="isSubmitting"
            :show-icon-after-text="true"
            @click.stop.prevent="goToChooseBilling"
        ></ButtonWithLoadingState>
    </div>
    </div>
</template>

<script>
import TransFilter from '../../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { userModalRoute } from '../router';
import ButtonWithLoadingState from '../../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import { translate } from '../../../../../qaamgo/helper/translate';
import { paymentManager } from '../../../../payment/payment-manager';
import { projectHelper } from '../../../../misc/project-helper';
import { pageStoreMixin } from '../../../../store/page-store';
import { testIsNonEmptyString } from '../../../../../lib/test-and-assert/test-base';
import { globalLogger } from '../../../../../qaamgo/helper/global-logger';
import { paymentApi } from '../../../../payment/payment-api';
import LoadingAnimation from '../../../../../qaamgo/modules/sat/components/LoadingAnimation';
import DataStatus from '../../../../../qaamgo/modules/sat/components/mixins/DataStatus';
import { pageNotification } from '../../../../../qaamgo/helper/notify';
import { satGlobals } from '../../../../../qaamgo/sat/helper/sat-globals';

// TODO replace this temporary variable with proper global config
const TEMP_IS_TEAMS_ENABLED = false;

export default {
    name: 'RegisterPlanSelectView',
    components: { LoadingAnimation, ButtonWithLoadingState },
    mixins: [TransFilter, DataStatus, pageStoreMixin],
    created() {
        if (paymentManager.isPricingInfoInitialized()) {
            this.setStatusDone();

            return;
        }

        paymentApi
            .getPricingInformation(true)
            .then((pricingInformation) => {
                paymentManager.setConfigFromPricingInformation(pricingInformation);
                this.setStatusDone();
            })
            .catch((error) => {
                pageNotification.showGenericErrorMessage();

                globalLogger.log('user modal - plan select - getPricingInformation - fail', error);
            });
    },
    beforeMount() {
        if (!testIsNonEmptyString(this.selectedPlanStore)) {
            this.store.userPaymentModal.setSelectedPlan('individual');
        }
    },
    mounted() {
        this.store.userPaymentModal.setSubmitting(false);
    },
    watch: {
        selectedPlan(plan) {
            this.store.userPaymentModal.setSelectedPlan(plan);
        },
        selectedPlanStore(plan) {
            if (this.selectedPlan !== plan) {
                this.selectedPlan = plan;
            }
        },
    },
    computed: {
        /** @return {Boolean} */
        isTrialEnabled() {
            return this.store.userPaymentModal.offerTrial;
        },
        /** @return {Boolean} */
        isSubmitting() {
            return this.store.userPaymentModal.isSubmitting;
        },
        selectedPlanStore() {
            return this.store.userPaymentModal.selectedBilling;
        },
        isIndividualSelected() {
            return this.selectedPlan === 'individual';
        },
        isTeamSelected() {
            return this.selectedPlan === 'team';
        },
        infoBoxTitleText() {
            if (this.isTrialEnabled) {
                return translate('Try %count% days free', { '%count%': satGlobals.getFreeTrialPeriod() });
            }

            return translate('Premium Account');
        },
        currency() {
            return this.store.userPaymentModal.currency;
        },
        infoBoxBodyText() {
            let minPrices = paymentManager.getSmallestPricesPerMonth();
            let minPrice = minPrices[this.currency] ?? minPrices.usd;

            if (this.isTrialEnabled) {
                return translate('After that you pay from <strong>%price%/month</strong>', {
                    '%price%': minPrice,
                });
            }

            return translate('You will pay from <strong>%price%/month</strong>', {
                '%price%': minPrice,
            });
        },
        features() {
            const featureTexts = [];

            featureTexts.push(translate('Access to all our Premium tools'));

            if (projectHelper.isPdf2go()) {
                featureTexts.push(translate('Premium access to our desktop app'));
            }

            if (!this.isTrialEnabled) {
                featureTexts.push(translate('Two week <strong>money back guarantee</strong>, no questions asked'));
            }

            featureTexts.push(translate('Free customer support'));
            featureTexts.push(
                translate('Join millions of other happy %projectName% users', {
                    '%projectName%': projectHelper.getProjectName(),
                })
            );

            return featureTexts;
        },
    },
    methods: {
      getPricingLink() {
        return satGlobals.getPricingUrl();
      },
      isLegacy() {
        let user =  this.store.user.getUser;
        if (projectHelper.isImg2go() === true && user.isLegacySubscription() === true) {
          return true;
        }

        return false;
      },
        goToChooseBilling() {
            // user modal - plan select - individual
            // user modal - plan select - team
            globalLogger.log('user modal - plan select - ' + this.selectedPlan);

            this.$router.push({
                name: userModalRoute.REGISTER_CHOOSE_BILLING,
            });
        },
    },
    data() {
        return {
            selectedPlan: 'individual',
            isTeamsEnabled: TEMP_IS_TEAMS_ENABLED,
        };
    },
};
</script>
